import React, { useState } from "react";
import { motion } from "framer-motion";
import { Helmet } from "react-helmet-async";
import toast from "react-hot-toast";
import api from "../hooks/useApi";

const SERVICES = [
  "Residential Construction",
  "Home Renovation",
  "Borwell Drilling",
  "Painting & Finishing",
  "Lenting Work",
  "Commercial Construction",
  "Government Contract",
  "Other",
];

export default function Contact() {
  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    service: SERVICES[0],
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const handleSubmit = async () => {
    if (!form.name || !form.phone)
      return toast.error("Please provide your name and phone number.");
    setSubmitting(true);
    try {
      await api.submitEnquiry(form);
      toast.success("Enquiry sent! We will contact you within 24 hours.");
      setForm({
        name: "",
        phone: "",
        email: "",
        service: SERVICES[0],
        message: "",
      });
    } catch {
      toast.error("Failed to send. Please call us directly.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Contact — NirmalRam Construction</title>
        <meta
          name="description"
          content="Contact NirmalRam Construction in Nandurbar for a free consultation, site visit, or cost estimate. Call, WhatsApp, or fill the form."
        />
      </Helmet>

      <section
        style={{
          background: "#0A0A0A",
          padding: "8rem 0 4rem",
          borderBottom: "1px solid rgba(201,168,76,0.12)",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <div className="grid-bg" />
        <div className="container" style={{ position: "relative", zIndex: 1 }}>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
          >
            <span className="section-tag">Get In Touch</span>
            <h1
              style={{
                fontFamily: "Cormorant Garamond,serif",
                marginBottom: "1rem",
              }}
            >
              Let's Build
              <br />
              Together
            </h1>
            <div className="section-divider" />
            <p style={{ color: "#8A8070", maxWidth: "520px", lineHeight: 1.8 }}>
              Ready to start your dream project? Contact us for a free
              consultation, site visit, and detailed cost estimate.
            </p>
          </motion.div>
        </div>
      </section>

      <section className="section">
        <div className="container">
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1.5fr",
              gap: "4rem",
              alignItems: "start",
            }}
          >
            {/* Info */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h3
                style={{
                  fontFamily: "Cormorant Garamond,serif",
                  fontSize: "1.6rem",
                  marginBottom: "1.5rem",
                }}
              >
                Reach Us Directly
              </h3>
              {[
                {
                  icon: "📍",
                  label: "Location",
                  value: "Nandurbar, Maharashtra, India",
                },
                { icon: "📞", label: "Phone", value: "+91 94036 46276" },
                {
                  icon: "✉",
                  label: "Email",
                  value: "nirmalramconstruction@gmail.com",
                },
                {
                  icon: "⏰",
                  label: "Working Hours",
                  value: "Monday – Saturday: 9:00 AM – 7:00 PM",
                },
              ].map((item) => (
                <div
                  key={item.label}
                  style={{
                    display: "flex",
                    gap: "1rem",
                    marginBottom: "1.5rem",
                    alignItems: "flex-start",
                  }}
                >
                  <div
                    style={{
                      width: "44px",
                      height: "44px",
                      background: "rgba(201,168,76,0.08)",
                      border: "1px solid rgba(201,168,76,0.2)",
                      borderRadius: "3px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: "1.1rem",
                      flexShrink: 0,
                    }}
                  >
                    {item.icon}
                  </div>
                  <div>
                    <div
                      style={{
                        fontSize: "0.72rem",
                        color: "#5A5450",
                        letterSpacing: "1.5px",
                        textTransform: "uppercase",
                        marginBottom: "0.2rem",
                      }}
                    >
                      {item.label}
                    </div>
                    <div style={{ fontSize: "0.95rem", color: "#F0EAE0" }}>
                      {item.value}
                    </div>
                  </div>
                </div>
              ))}

              <a
                href="https://wa.me/919403646276?text=Hello%20NirmalRam%20Construction%2C%20I%20am%20interested%20in%20your%20services."
                target="_blank"
                rel="noreferrer"
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "0.75rem",
                  background: "#25D366",
                  color: "#fff",
                  padding: "0.85rem 1.5rem",
                  borderRadius: "3px",
                  fontWeight: 600,
                  fontSize: "0.9rem",
                  marginTop: "1rem",
                  textDecoration: "none",
                  width: "fit-content",
                }}
              >
                💬 Chat on WhatsApp
              </a>

              {/* Map placeholder */}
              <div
                style={{
                  marginTop: "2rem",
                  background: "#111",
                  border: "1px solid rgba(201,168,76,0.15)",
                  borderRadius: "3px",
                  height: "220px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexDirection: "column",
                  gap: "0.75rem",
                }}
              >
                <span style={{ fontSize: "2.5rem" }}>📍</span>
                <p
                  style={{
                    color: "#5A5450",
                    fontSize: "0.85rem",
                    textAlign: "center",
                  }}
                >
                  Nandurbar, Maharashtra
                  <br />
                  <a
                    href="https://maps.google.com/?q=Nandurbar,Maharashtra"
                    target="_blank"
                    rel="noreferrer"
                    style={{
                      color: "#C9A84C",
                      textDecoration: "none",
                      fontSize: "0.8rem",
                    }}
                  >
                    Open in Google Maps →
                  </a>
                </p>
              </div>
            </motion.div>

            {/* Form */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <div
                style={{
                  background: "#0E0E0E",
                  border: "1px solid rgba(201,168,76,0.15)",
                  padding: "2.5rem",
                  borderRadius: "3px",
                }}
              >
                <h3
                  style={{
                    fontFamily: "Cormorant Garamond,serif",
                    fontSize: "1.4rem",
                    marginBottom: "2rem",
                  }}
                >
                  Send Us an Enquiry
                </h3>
                <div className="form-row">
                  <div className="form-group">
                    <label>Full Name *</label>
                    <input
                      value={form.name}
                      onChange={update("name")}
                      placeholder="Your name"
                    />
                  </div>
                  <div className="form-group">
                    <label>Phone *</label>
                    <input
                      type="tel"
                      value={form.phone}
                      onChange={update("phone")}
                      placeholder="+91 XXXXX XXXXX"
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label>Email</label>
                  <input
                    type="email"
                    value={form.email}
                    onChange={update("email")}
                    placeholder="your@email.com"
                  />
                </div>
                <div className="form-group">
                  <label>Service Needed</label>
                  <select value={form.service} onChange={update("service")}>
                    {SERVICES.map((s) => (
                      <option key={s}>{s}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Message</label>
                  <textarea
                    value={form.message}
                    onChange={update("message")}
                    placeholder="Tell us about your project — location, size, requirements..."
                  />
                </div>
                <button
                  className="btn-gold"
                  style={{
                    width: "100%",
                    justifyContent: "center",
                    opacity: submitting ? 0.7 : 1,
                  }}
                  onClick={handleSubmit}
                  disabled={submitting}
                >
                  {submitting ? "Sending..." : "Send Enquiry →"}
                </button>
                <p
                  style={{
                    color: "#5A5450",
                    fontSize: "0.8rem",
                    textAlign: "center",
                    marginTop: "1rem",
                  }}
                >
                  We will contact you within 24 hours.
                </p>
              </div>
            </motion.div>
          </div>
        </div>
        <style>{`@media(max-width:900px){.container>div[style*="1.5fr"]{grid-template-columns:1fr!important;}}`}</style>
      </section>
    </>
  );
}
